// src/components/AIAgentPage.js
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Select, { components } from 'react-select';
import { useData } from '../context/DataContext';
import { useJobs } from '../context/jobContext';
import styles from './AIAgentPage.module.css'; // New CSS module
import SortableTable from '../utils/SortableTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStopCircle,
  faTrashAlt,
  faUserAstronaut,
  faPlus,
  faList,
  faMagic,
} from '@fortawesome/free-solid-svg-icons';
import AgentChatWindow from './AgentChatWindow';
import ConfirmationDialog from './ConfirmationDialog'; // Import your modal
import JobDetailsModal from './JobDetailsModal'; // New Modal component
import { countryList } from '../utils/CountryList';
import Spinner from './Spinner';

// Hardcoded task options for the dropdown
const AGENT_TASK_OPTIONS = [
  {
    value: 'DISCOVERY_FULL_CYCLE',
    label: 'Autonomous Discovery (High Qualified Leads)',
  },
  { value: 'CHANNEL_ANALYSIS', label: 'Channel Analysis' },
  { value: 'DEEP_CONTENT_ANALYSIS', label: 'Deep Content Analysis' },
  { value: 'RELATIONSHIP_SUMMARY', label: 'Relationship Summary' },
  { value: 'KNOWLEDGE_SUMMARY', label: 'Knowledge Summary' },
  { value: 'SEND_MESSAGES', label: 'Send Messages' },
];

const SAVED_PROMPTS_OPTIONS = [
  { value: 'p1', label: '📂 Saved: Analyze New Leads' },
  { value: 'p2', label: '📂 Saved: Follow-up on Ghosted' },
  { value: 'p3', label: '📂 Saved: Negotiate Deals' },
];

const AIAgentPage = () => {
  // --- Get global state from contexts ---
  const {
    queryResults,
    setQueryResults,
    lastQuery,
    runSingleQuery,
    runMultipleQuery,
    // --- NEW: Import Cache State and Dispatchers ---
    aiAgentPageCache,
    updateAiAgentPageCache,
    clearAiAgentPageCache,
  } = useData();
  const {
    agentJobHistory,
    isJobDetailsModalOpen,
    startAgentJob,
    fetchAgentJobHistory,
    fetchAgentJobDetails,
    setSelectedJobDetails,
    cancelAgentJob, // From Context
    deleteAgentJob, // From Context
    agentChatsList, // <--- Get list
    getAgentChats, // <--- Get fetcher
    setActiveAgentChat, // <--- Get setter
    activeAgentChatId, // <--- Get current ID
  } = useJobs();

  // --- Derive State from Cache ---
  // We use useMemo to merge the cache with default values
  const cachedState = useMemo(() => {
    return {
      selectedInfluencers: [],
      selectedTask: null,
      jobName: '',
      prompt: '',
      ...aiAgentPageCache, // Override defaults with cached values
    };
  }, [aiAgentPageCache]);

  // Destructure for easier usage in the render
  const { selectedInfluencers, selectedTask, jobName, prompt } = cachedState;

  // --- Local state for form inputs and UI ---
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState('');
  const [selectedJobName, setSelectedJobName] = useState('');

  // Autonomous Discovery states
  const [discoveryCountries, setDiscoveryCountries] = useState([
    { value: 'US', label: 'United States' },
  ]);
  const [discoveryMinSubs, setDiscoveryMinSubs] = useState(10000);
  const [discoveryMaxSubs, setDiscoveryMaxSubs] = useState(500000);
  const [discoveryTargetCount, setDiscoveryTargetCount] = useState(5);

  // --- State for Confirmation Modal ---
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: null, // 'cancel' or 'delete'
    jobId: null,
    jobName: '',
  });
  const [activeTab, setActiveTab] = useState('manual'); // 'manual' | 'autopilot'

  // --- Helper to Generate Job Name ---
  // Moved out of useEffect to be used in event handlers
  const generateJobName = (task, influencers) => {
    if (task && influencers.length > 0) {
      const date = new Date().toLocaleDateString([], {
        month: 'short',
        day: 'numeric',
      });
      return `${task.label} for ${influencers.length} influencer(s) - ${date}`;
    }
    return '';
  };

  console.log(
    `[AIAgentPage Render] isJobDetailsModalOpen is: ${isJobDetailsModalOpen}`,
  );

  // --- Load Chat History on Mount ---
  useEffect(() => {
    // Load Job History
    fetchAgentJobHistory();
    // Load Chat Session History
    getAgentChats();
  }, [fetchAgentJobHistory, getAgentChats]);

  // --- Fetch job history on component mount ---
  useEffect(() => {
    setLoadingMessage('Loading job history...');
    setIsLoading(true);
    fetchAgentJobHistory()
      .catch((e) => setError('Failed to load history.'))
      .finally(() => setIsLoading(false));
  }, [fetchAgentJobHistory]);

  // --- Memoize options for react-select components ---
  const influencerSelectOptions = useMemo(() => {
    return (queryResults || [])
      .filter((inf) => inf && inf.influencerId)
      .map((inf) => ({
        value: String(inf.influencerId),
        label: inf.name || inf.nickname || `ID: ${inf.influencerId}`,
      }));
  }, [queryResults]);

  // --- Input Change Handlers (Update Cache) ---

  const handleInfluencersChange = (newValue) => {
    const updatedInfluencers = newValue || [];
    // Auto-update job name in cache based on new selection
    const newName = generateJobName(selectedTask, updatedInfluencers);

    updateAiAgentPageCache({
      selectedInfluencers: updatedInfluencers,
      jobName: newName,
    });
  };

  const handleTaskChange = (newValue) => {
    // Auto-update job name in cache based on new task
    const newName = generateJobName(newValue, selectedInfluencers);

    updateAiAgentPageCache({
      selectedTask: newValue,
      jobName: newName,
    });
  };

  const handleJobNameChange = (e) => {
    updateAiAgentPageCache({ jobName: e.target.value });
  };

  const handlePromptChange = (e) => {
    updateAiAgentPageCache({ prompt: e.target.value });
  };

  // --- Transform Chat List for Dropdown ---
  const savedSessionsOptions = useMemo(() => {
    // Force check if it is an array
    const list = Array.isArray(agentChatsList) ? agentChatsList : [];
    return list.map((chat) => ({
      value: chat.chatId,
      label:
        chat.titleChat ||
        `Session #${chat.chatId} (${new Date(chat.updatedAt).toLocaleDateString()})`,
    }));
  }, [agentChatsList]);

  // --- Event Handlers ---

  // --- NEW: Handle Start New Chat ---
  const handleNewChat = () => {
    // Setting ID to null resets the ChatWindow to "Fresh" state
    setActiveAgentChat(null);

    // 2. Clear the form inputs cache (Clears Influencers, Task, Prompt, Job Name)
    clearAiAgentPageCache();
  };

  // --- Session Dropdown Logic ---
  const sessionOptions = useMemo(() => {
    // Force check if it is an array
    const list = Array.isArray(agentChatsList) ? agentChatsList : [];
    return list.map((chat) => ({
      value: chat.chatId,
      label:
        chat.titleChat ||
        `Session ${chat.chatId} (${new Date(chat.updatedAt).toLocaleDateString()})`,
    }));
  }, [agentChatsList]);

  // --- Handle Chat Selection ---
  const handleSessionChange = (option) => {
    if (option) {
      console.log('Switching to Agent Session:', option.value);
      setActiveAgentChat(option.value);
    } else {
      setActiveAgentChat(null);
    }
  };

  const handleExecuteJob = useCallback(async () => {
    setError('');
    // Determine limit and label based on task type
    const isDeepContent = selectedTask?.value === 'DEEP_CONTENT_ANALYSIS';
    const limit = isDeepContent ? 20 : 50;
    const taskLabel = selectedTask?.label || 'the selected task';

    if (selectedInfluencers.length > limit) {
      setError(
        `The AI Agent can handle a maximum of ${limit} influencers for '${taskLabel}'. You have selected ${selectedInfluencers.length}. Please reduce the selection.`,
      );
      return;
    }

    setLoadingMessage('Executing task... This may take a moment.');
    setIsLoading(true);
    try {
      const payload = {
        jobName,
        jobType: selectedTask.value,
        influencerIds: selectedInfluencers.map((inf) => inf.value),
        taskParameters: {
          ...(selectedTask.value === 'SEND_MESSAGES' && { prompt }),
          ...(selectedTask.value === 'DISCOVERY_FULL_CYCLE' && {
            countries: discoveryCountries.map((c) => c.value), // Array of country codes
            minSubs: discoveryMinSubs,
            maxSubs: discoveryMaxSubs,
            targetCount: discoveryTargetCount,
          }),
        },
      };

      await startAgentJob(payload);

      // --- NEW: Clear the form inputs after successful start ---
      clearAiAgentPageCache();

      // Refresh history
      await fetchAgentJobHistory();
      if (lastQuery) {
        let freshResults = [];
        if (lastQuery.type === 'single') {
          freshResults = await runSingleQuery(lastQuery.params);
        } else if (lastQuery.type === 'multiple') {
          freshResults = await runMultipleQuery(lastQuery.params);
        }
        setQueryResults(freshResults);
      }
    } catch (e) {
      setError(e.message || 'Failed to start job.');
    } finally {
      setIsLoading(false);
    }
  }, [
    jobName,
    selectedTask,
    selectedInfluencers,
    prompt,
    startAgentJob,
    fetchAgentJobHistory,
    lastQuery,
    runSingleQuery,
    runMultipleQuery,
    setQueryResults,
    clearAiAgentPageCache, // Dependency
  ]);

  const handleActionClick = (e, job, type) => {
    e.stopPropagation(); // Prevent row click (opening details)
    setConfirmModal({
      isOpen: true,
      type: type,
      jobId: job.id,
      jobName: job.jobName,
    });
  };

  const handleConfirmAction = async () => {
    if (confirmModal.type === 'cancel') {
      await cancelAgentJob(confirmModal.jobId);
    } else if (confirmModal.type === 'delete') {
      await deleteAgentJob(confirmModal.jobId);
    }
    setConfirmModal({ isOpen: false, type: null, jobId: null, jobName: '' });
  };

  const handleCloseModal = () => {
    setConfirmModal({ isOpen: false, type: null, jobId: null, jobName: '' });
  };

  const handleRowClick = useCallback(
    async (job) => {
      // UX IMPROVEMENT: Show spinner immediately to bridge the "gap"
      setLoadingMessage('Retrieving job details...');
      setIsLoading(true);

      try {
        // Capture name for the modal title
        setSelectedJobName(job.jobName);

        // Await the fetch so the spinner stays until data is ready
        await fetchAgentJobDetails(job.id, job.jobType);
      } catch (err) {
        console.error('Error fetching details:', err);
        setError('Could not load details for this job.');
      } finally {
        // Hide spinner only after data is fetched (Modal will open via context state)
        setIsLoading(false);
      }
    },
    [fetchAgentJobDetails],
  );

  const MenuList = (props) => {
    const { options, getValue, selectProps } = props;
    const values = getValue();
    const allSelected = values.length === options.length;

    const handleSelectAll = () => {
      if (allSelected) {
        handleInfluencersChange([]);
      } else {
        handleInfluencersChange(options);
      }
    };

    return (
      <components.MenuList {...props}>
        <div className={styles.selectAllContainer}>
          <button
            type="button"
            className={styles.selectAllButton}
            onClick={handleSelectAll}
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
        </div>
        {props.children}
      </components.MenuList>
    );
  };

  const TaskOption = (props) => {
    return (
      <div
        // If the option is disabled, show the tooltip
        title={
          props.data.isDisabled
            ? 'This feature is under development. Please check back later.'
            : ''
        }
      >
        <components.Option {...props} />
      </div>
    );
  };

  // --- COMBINED STYLES: Portal Z-Index + Max Height for List ---
  const customSelectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }), // Fixes overflow/cutoff
    valueContainer: (provided) => ({
      ...provided,
      maxHeight: '100px', // Limits height of the selected tags area
      overflowY: 'auto',
      alignItems: 'flex-start',
    }),
    input: (provided) => ({
      ...provided,
      marginTop: '2px',
    }),
  };

  // --- Reusable Render Helper for Influencer Select ---
  const renderInfluencerSelect = (customClassName) => {
    const isDiscoveryTask = selectedTask?.value === 'DISCOVERY_FULL_CYCLE';

    return (
      <Select
        isMulti
        closeMenuOnSelect={false}
        options={influencerSelectOptions}
        // Visually clear the influencers when discovery is selected, but keep them in state
        value={isDiscoveryTask ? [] : selectedInfluencers}
        onChange={handleInfluencersChange}
        placeholder={
          isDiscoveryTask
            ? 'Not required for Discovery task'
            : 'Select Influencers...'
        }
        isDisabled={isDiscoveryTask}
        className={customClassName}
        classNamePrefix="select"
        menuPortalTarget={document.body}
        styles={customSelectStyles}
        components={{ MenuList }}
      />
    );
  };

  // --- Columns definition for the SortableTable ---
  const jobHistoryColumns = useMemo(
    () => [
      {
        // NEW ACTION COLUMN AT THE START
        header: '',
        accessorKey: 'actions',
        size: 50,
        cell: ({ row }) => {
          const status = row.original.status;
          const isActive = ['queued', 'running', 'pending'].includes(status);

          if (isActive) {
            return (
              <button
                className={`${styles.actionButton} ${styles.stopBtn}`}
                onClick={(e) => handleActionClick(e, row.original, 'cancel')}
                title="Stop Job"
              >
                <FontAwesomeIcon icon={faStopCircle} />
              </button>
            );
          } else {
            return (
              <button
                className={`${styles.actionButton} ${styles.trashBtn}`}
                onClick={(e) => handleActionClick(e, row.original, 'delete')}
                title="Delete Job Record"
              >
                <FontAwesomeIcon icon={faTrashAlt} />
              </button>
            );
          }
        },
      },
      { header: 'Job Name', accessorKey: 'jobName', size: 350 },
      { header: 'Job Type', accessorKey: 'jobType', size: 200 },
      {
        header: 'Status',
        accessorKey: 'status',
        size: 140,
        cell: ({ row }) => (
          <span
            className={`${styles.statusBadge} ${styles[row.original.status]}`}
          >
            {row.original.status}
          </span>
        ),
      },
      {
        header: 'Done',
        accessorKey: 'progress',
        size: 100,
        cell: ({ row }) =>
          `${row.original.processedItems}/${row.original.totalItems}`,
      },
      { header: 'Message', accessorKey: 'statusMessage', size: 700 },
      {
        header: 'Started On',
        accessorKey: 'createdAt',
        size: 240,
        cell: (info) => new Date(info.getValue()).toLocaleString(),
      },
      {
        header: 'Duration',
        accessorKey: 'durationSeconds',
        size: 140,
        cell: ({ row }) => {
          const val = row.original.durationSeconds;

          // Handle cases where job is running or barely started
          if (val === null || val === undefined) {
            if (row.original.status === 'completed') return '< 1s';
            return '-';
          }

          const hours = Math.floor(val / 3600);
          const minutes = Math.floor((val % 3600) / 60);
          const seconds = val % 60;

          if (hours > 0) {
            // Format: 1h 20m 30s
            return `${hours}h ${minutes}m ${seconds}s`;
          } else if (minutes > 0) {
            // Format: 20m 30s
            return `${minutes}m ${seconds}s`;
          } else {
            // Format: 30s
            return `${seconds}s`;
          }
        },
      },
      {
        header: 'Cost',
        accessorKey: 'totalCostUsd',
        size: 120,
        cell: ({ row }) => {
          const cost = parseFloat(row.original.totalCostUsd);
          // Display with 4 decimal places for precision (e.g. $0.0402)
          return cost > 0 ? `$${cost.toFixed(4)}` : '-';
        },
      },
    ],
    [cancelAgentJob, deleteAgentJob],
  );

  // --- UI Render Logic ---
  const isExecuteDisabled =
    !selectedTask ||
    !(jobName || '').trim() || // <-- FIX: Safe string evaluation
    isLoading ||
    (selectedTask.value !== 'DISCOVERY_FULL_CYCLE' &&
      selectedInfluencers.length === 0);

  const showPrompt = selectedTask?.value === 'SEND_MESSAGES';

  return (
    <div className={styles.aiAgentPage}>
      {isLoading && <Spinner message={loadingMessage} />}

      <JobDetailsModal
        isOpen={isJobDetailsModalOpen}
        onClose={() => setSelectedJobDetails(null)}
        jobName={selectedJobName}
      />

      <ConfirmationDialog
        isOpen={confirmModal.isOpen}
        message={
          confirmModal.type === 'cancel'
            ? `Are you sure you want to stop the job "${confirmModal.jobName}"? The backend process will be terminated.`
            : `Are you sure you want to delete the history for "${confirmModal.jobName}"? This cannot be undone.`
        }
        onConfirm={handleConfirmAction}
        onCancel={handleCloseModal}
        isDanger={true}
      />

      <h2>
        <FontAwesomeIcon icon={faUserAstronaut} /> AI Agent Control Panel
      </h2>

      {/* --- NEW: Tab Switcher --- */}
      <div className={styles.tabContainer}>
        <button
          className={`${styles.tabButton} ${activeTab === 'manual' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('manual')}
        >
          <FontAwesomeIcon icon={faList} /> Task Runner
        </button>
        <button
          className={`${styles.tabButton} ${activeTab === 'autopilot' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('autopilot')}
        >
          <FontAwesomeIcon icon={faMagic} /> AI Auto-Pilot (Beta)
        </button>
      </div>

      <div className={styles.inputContainer}>
        {/* --- MANUAL MODE: Side-by-Side Layout (PRESERVED) --- */}
        {activeTab === 'manual' && (
          <div className={styles.manualControls}>
            <div className={styles.inputRow}>
              <Select
                options={AGENT_TASK_OPTIONS}
                value={selectedTask}
                onChange={handleTaskChange}
                placeholder="Select a Task..."
                className={styles.taskSelect}
                classNamePrefix="select"
                menuPortalTarget={document.body}
                styles={customSelectStyles}
                components={{ Option: TaskOption }}
                isOptionDisabled={(option) => option.isDisabled}
              />
              {/* Influencer Select: Uses dedicated class for side-by-side spacing */}
              {renderInfluencerSelect(styles.influencerSelect)}
            </div>
            {selectedTask?.value === 'DISCOVERY_FULL_CYCLE' && (
              <div className={styles.discoveryInputsRow}>
                <div className={`${styles.queryField} ${styles.countryField}`}>
                  <label>Target Countries</label>
                  <Select
                    isMulti
                    options={countryList}
                    value={discoveryCountries}
                    onChange={setDiscoveryCountries}
                    classNamePrefix="select"
                    styles={customSelectStyles}
                    placeholder="Select Countries..."
                    menuPortalTarget={document.body}
                  />
                </div>
                <div className={styles.queryField}>
                  <label>Min Subscribers</label>
                  <input
                    type="number"
                    value={discoveryMinSubs}
                    onChange={(e) => setDiscoveryMinSubs(e.target.value)}
                    className={styles.jobNameInput}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
                <div className={styles.queryField}>
                  <label>Max Subscribers</label>
                  <input
                    type="number"
                    value={discoveryMaxSubs}
                    onChange={(e) => setDiscoveryMaxSubs(e.target.value)}
                    className={styles.jobNameInput}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
                <div className={styles.queryField}>
                  <label>Target Influencers (1-20)</label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={discoveryTargetCount}
                    onChange={(e) =>
                      setDiscoveryTargetCount(
                        Math.min(20, Math.max(1, e.target.value)),
                      )
                    }
                    className={styles.jobNameInput}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            )}
            {showPrompt && (
              <div className={styles.promptRow}>
                <textarea
                  value={prompt}
                  onChange={handlePromptChange}
                  placeholder="Enter message..."
                  className={styles.promptTextarea}
                  rows={4}
                />
              </div>
            )}

            <div className={styles.inputRow}>
              <input
                type="text"
                value={jobName}
                onChange={handleJobNameChange}
                placeholder="Enter a name for this job..."
                className={styles.jobNameInput}
              />
              <button
                onClick={handleExecuteJob}
                className={styles.executeButton}
                disabled={isExecuteDisabled}
              >
                Execute Task
              </button>
            </div>
          </div>
        )}

        {/* --- AUTO-PILOT MODE: Full Width + Chat --- */}
        {activeTab === 'autopilot' && (
          <div className={styles.chatWrapper}>
            <div
              className={styles.inputRow}
              style={{
                marginBottom: '10px',
                display: 'flex',
                gap: '10px',
                alignItems: 'flex-start',
              }}
            >
              <div style={{ flex: 2 }}>
                {renderInfluencerSelect(styles.influencerSelectFull)}
              </div>

              {/* --- NEW CHAT CONTROLS --- */}
              <div style={{ flex: 1, display: 'flex', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                  <Select
                    options={sessionOptions}
                    value={
                      sessionOptions.find(
                        (opt) => opt.value === activeAgentChatId,
                      ) || null
                    }
                    onChange={handleSessionChange}
                    placeholder="Load Previous Session..."
                    className={styles.savedPromptsSelect}
                    classNamePrefix="select"
                    isClearable={true}
                    styles={customSelectStyles}
                  />
                </div>
                {/* THE NEW BUTTON */}
                <button
                  onClick={handleNewChat}
                  className={styles.newChatButton}
                  title="Start New Chat"
                  disabled={!activeAgentChatId} // Disable if already on new chat
                >
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              </div>
            </div>

            <AgentChatWindow selectedInfluencers={selectedInfluencers} />
          </div>
        )}
      </div>

      {error && <div className={styles.errorBanner}>{error}</div>}

      {/* --- BOTTOM SECTION: Control Panel Table (PRESERVED PROPS) --- */}
      <div className={styles.controlPanel}>
        <h3>Job History</h3>
        <SortableTable
          data={Array.isArray(agentJobHistory) ? agentJobHistory : []}
          columns={jobHistoryColumns}
          onRowClick={handleRowClick}
          enableStickyHeader={true}
          stickyFirstColumn={true}
        />
      </div>
    </div>
  );
};

export default AIAgentPage;
